import { Fragment, type JSX, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * Tiny safe-by-construction markdown renderer for the volunteer-facing
 * explainer text on attestation rows. Supports inline `[text](href)`
 * links and `**bold**` runs across paragraphs split by blank lines.
 *
 * "Safe by construction" means the function never returns raw HTML.
 * Links are rendered through `<Link to=...>` for in-app `/me/*` and
 * `/sign-in` paths and through `<a target="_blank">` for `http(s)://`
 * URLs only — anything else (`javascript:`, `data:`, `file:`, schemeless
 * fragments) falls back to the visible link text and the href is
 * dropped, so a broken-or-hostile admin entry never escapes the markup
 * boundary.
 *
 * The explainer is admin-authored and admins are trusted with markdown
 * already, but rendering arbitrary HTML opens up XSS surface that this
 * register has otherwise avoided. The whitelisted-token approach here
 * gives volunteers clickable links + bold without that surface.
 */
export function renderMarkdownExplainer(text: string): JSX.Element {
  const paragraphs = text.split(/\n{2,}/);
  return (
    <>
      {paragraphs.map((para, idx) => (
        <p key={idx} className="me-attestation-explainer">
          {renderInline(para)}
        </p>
      ))}
    </>
  );
}

const INLINE_TOKEN = /\[([^\]]+)\]\(([^\s)]+)\)|\*\*([^*]+)\*\*/g;

function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let lastIndex = 0;
  let i = 0;
  for (const match of text.matchAll(INLINE_TOKEN)) {
    const start = match.index ?? 0;
    if (start > lastIndex) {
      out.push(text.slice(lastIndex, start));
    }
    if (match[1] !== undefined && match[2] !== undefined) {
      out.push(<LinkOrText key={`l-${i}`} label={match[1]} href={match[2]} />);
    } else if (match[3] !== undefined) {
      out.push(<strong key={`b-${i}`}>{match[3]}</strong>);
    }
    lastIndex = start + match[0].length;
    i += 1;
  }
  if (lastIndex < text.length) {
    out.push(text.slice(lastIndex));
  }
  // React keys: wrap raw strings in fragments so siblings keep stable
  // positions; React tolerates a mixed array, but explicit fragments
  // make the test output cleaner.
  return out.map((node, idx) =>
    typeof node === 'string' ? <Fragment key={`t-${idx}`}>{node}</Fragment> : node,
  );
}

interface LinkOrTextProps {
  label: string;
  href: string;
}

function LinkOrText({ label, href }: LinkOrTextProps): JSX.Element {
  if (href.startsWith('/me') || href.startsWith('/sign-in') || href.startsWith('/ack/')) {
    return <Link to={href}>{label}</Link>;
  }
  if (/^https?:\/\//i.test(href)) {
    return (
      <a href={href} target="_blank" rel="noreferrer noopener">
        {label}
      </a>
    );
  }
  return <>{label}</>;
}
